import { useState, useEffect, useCallback } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { getInvitationDetails, acceptInvitation } from './utils/apiContentSharing';
import { isTokenValid } from './utils/auth';
import {
  pickSharerName,
  pickContentTitle,
  pickChapterLabel,
  pickChapterId,
  pickPersonalMessage,
  isAlreadyAccepted,
  buildContentRedirect,
} from './utils/invitationFields';

function InvitationPage() {
  const { token } = useParams();
  const navigate = useNavigate();

  const [invitation, setInvitation] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [accepting, setAccepting] = useState(false);
  const [acceptError, setAcceptError] = useState('');
  const isLoggedIn = isTokenValid();

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const data = await getInvitationDetails(token);
        if (!cancelled) setInvitation(data);
      } catch (err) {
        if (cancelled) return;
        const status = err.response?.status;
        if (status === 404 || status === 410) {
          setError('This invitation link is invalid or has expired.');
        } else if (err.request) {
          setError('Network error: Could not load the invitation.');
        } else {
          setError('Could not load this invitation. Please try again later.');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [token]);

  const handleAccept = useCallback(async () => {
    setAccepting(true);
    setAcceptError('');
    try {
      const result = await acceptInvitation(token);
      const target = buildContentRedirect(result, invitation);
      navigate(target);
    } catch (err) {
      if (err.response?.status === 409 || isAlreadyAccepted(err.response?.data)) {
        const target = buildContentRedirect(null, invitation);
        navigate(target);
      } else if (err.response?.status === 401) {
        navigate(`/login?next=${encodeURIComponent(`/invitations/${token}`)}`);
      } else {
        setAcceptError('Could not accept the invitation. Please try again.');
      }
    } finally {
      setAccepting(false);
    }
  }, [token, invitation, navigate]);

  if (loading) return <CenterLayout><Spinner label="Loading invitation..." /></CenterLayout>;
  if (error) return <CenterLayout><ErrorPanel message={error} /></CenterLayout>;

  const sharer = pickSharerName(invitation);
  const title = pickContentTitle(invitation);
  const chapterLabel = pickChapterLabel(invitation);
  const message = pickPersonalMessage(invitation);
  const isHighlight = !!pickChapterId(invitation);

  return (
    <CenterLayout>
      <div className="bg-white dark:bg-background-dark/50 border border-gray-200 dark:border-gray-800 rounded-lg p-8 shadow-lg w-full max-w-md">
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">You've been invited!</h1>
        <p className="text-sm text-gray-600 dark:text-gray-400 mb-6">
          <span className="font-medium">{sharer}</span> wants to share content with you on Dissona.
        </p>

        <div className="mb-6 p-4 rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50">
          <div className="flex flex-wrap items-center gap-2 mb-2">
            <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-blue-100 text-blue-800 dark:bg-blue-900/20 dark:text-blue-400">
              Book
            </span>
            {isHighlight && (
              <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-purple-100 text-purple-800 dark:bg-purple-900/20 dark:text-purple-400">
                Chapter highlight
              </span>
            )}
          </div>
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white">{title}</h2>
          {isHighlight && chapterLabel && (
            <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">{chapterLabel}</p>
          )}
          {isHighlight && (
            <p className="mt-2 text-xs text-gray-500 dark:text-gray-400 italic">
              You'll be taken to this chapter on first open. Accepting this invitation also grants access to the entire book.
            </p>
          )}
        </div>

        {message && (
          <blockquote className="mb-6 border-l-4 border-primary pl-4 text-sm italic text-gray-700 dark:text-gray-300">
            "{message}"
          </blockquote>
        )}

        {acceptError && (
          <div className="mb-4 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
            <p className="text-sm text-red-800 dark:text-red-200">{acceptError}</p>
          </div>
        )}

        <InvitationActions
          isLoggedIn={isLoggedIn}
          accepting={accepting}
          onAccept={handleAccept}
          token={token}
        />
      </div>
    </CenterLayout>
  );
}

export default InvitationPage;

/* ----- internal layout helpers (kept in-file to avoid extra components) ----- */

function CenterLayout({ children }) {
  return (
    <div className="min-h-screen flex items-center justify-center bg-background-light dark:bg-background-dark px-4 py-12">
      {children}
    </div>
  );
}

function Spinner({ label }) {
  return (
    <div className="text-center" role="status" aria-live="polite">
      <div className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-solid border-primary border-r-transparent" />
      <p className="mt-4 text-gray-600 dark:text-gray-400">{label}</p>
    </div>
  );
}

function ErrorPanel({ message }) {
  return (
    <div className="bg-white dark:bg-background-dark/50 border border-red-200 dark:border-red-800 rounded-lg p-8 shadow-lg w-full max-w-md text-center">
      <h1 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">Invitation unavailable</h1>
      <p className="text-sm text-red-700 dark:text-red-300 mb-6">{message}</p>
      <Link to="/dashboard" className="inline-block px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary/90 transition-colors">
        Go to Dashboard
      </Link>
    </div>
  );
}

function InvitationActions({ isLoggedIn, accepting, onAccept, token }) {
  if (isLoggedIn) {
    return (
      <button
        type="button"
        onClick={onAccept}
        disabled={accepting}
        className="w-full h-12 rounded-lg bg-primary px-6 text-base font-bold text-white hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
      >
        {accepting ? 'Accepting...' : 'Accept Share'}
      </button>
    );
  }
  return (
    <div className="space-y-3">
      <Link
        to={`/register?share_invitation_token=${encodeURIComponent(token)}`}
        className="block w-full h-12 rounded-lg bg-primary px-6 text-base font-bold text-white hover:bg-primary/90 transition-colors text-center leading-[3rem]"
      >
        Register to Access
      </Link>
      <Link
        to={`/login?next=${encodeURIComponent(`/invitations/${token}`)}`}
        className="block w-full h-12 rounded-lg border border-gray-300 dark:border-gray-600 px-6 text-base font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors text-center leading-[3rem]"
      >
        Login to Accept
      </Link>
    </div>
  );
}
